import { useEffect, useMemo, useState } from 'react';
import { EMPTY_CELL_LABEL } from '@/modules/payroll/utils/payrollFormat.utils';

const PAGE_SIZE = 100;

type SortState = { column: string; direction: 'asc' | 'desc' };

const cellCollator = new Intl.Collator('en-AU', {
  numeric: true,
  sensitivity: 'base',
});

/** Empty cells always sort last, whichever the direction. */
const compareCells = (
  a: string | undefined,
  b: string | undefined,
  direction: SortState['direction'],
): number => {
  if (!a || !b) {
    return a === b ? 0 : a ? -1 : 1;
  }
  const result = cellCollator.compare(a, b);
  return direction === 'asc' ? result : -result;
};

type PayrollDataTableProps = {
  columns: string[];
  rows: string[][];
  emptyMessage?: string;
  /** Tailwind max-height class for the scroll area. */
  maxHeightClassName?: string;
  /** Let column headers sort the rows (click again to reverse). */
  sortable?: boolean;
};

/**
 * Plain string table with a sticky header, horizontal scroll and client-side
 * paging, used for reports whose columns are only known at runtime.
 */
const PayrollDataTable = ({
  columns,
  rows,
  emptyMessage = 'No rows to show.',
  maxHeightClassName = 'max-h-[60vh]',
  sortable = false,
}: PayrollDataTableProps) => {
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<SortState | null>(null);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));

  useEffect(() => {
    setPage(0);
  }, [rows, sort]);

  // Columns vary between reports; a sort on a column that is gone is ignored.
  const sortColumnIndex = sort ? columns.indexOf(sort.column) : -1;
  const sortDirection = sort?.direction ?? 'asc';

  const sortedRows = useMemo(() => {
    if (sortColumnIndex < 0) {
      return rows;
    }
    return [...rows].sort((a, b) =>
      compareCells(a[sortColumnIndex], b[sortColumnIndex], sortDirection),
    );
  }, [rows, sortColumnIndex, sortDirection]);

  const pageRows = useMemo(
    () => sortedRows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [sortedRows, page],
  );

  const handleSort = (column: string) => {
    setSort((current) =>
      current?.column === column
        ? { column, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: 'asc' },
    );
  };

  if (rows.length === 0) {
    return <p className="py-6 text-sm text-gray-500">{emptyMessage}</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      <div
        className={`overflow-auto rounded-md border border-gray-200 ${maxHeightClassName}`}
      >
        <table className="min-w-full border-collapse text-left text-sm">
          <thead className="sticky top-0 z-10 bg-gray-50">
            <tr>
              {columns.map((column, columnIndex) => (
                <th
                  key={column}
                  className="border-b border-gray-200 px-3 py-2 font-medium whitespace-nowrap text-gray-700"
                  aria-sort={
                    sortColumnIndex === columnIndex
                      ? sortDirection === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                >
                  {sortable ? (
                    <button
                      type="button"
                      className="hover:text-[#03406A]"
                      onClick={() => handleSort(column)}
                    >
                      {column}
                      {sortColumnIndex === columnIndex
                        ? sortDirection === 'asc'
                          ? ' ▲'
                          : ' ▼'
                        : ''}
                    </button>
                  ) : (
                    column
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, rowIndex) => (
              <tr
                key={`${page}-${rowIndex}`}
                className="odd:bg-white even:bg-gray-50/50"
              >
                {columns.map((column, columnIndex) => (
                  <td
                    key={column}
                    className="max-w-[360px] truncate border-b border-gray-100 px-3 py-2 text-gray-900"
                    title={row[columnIndex] || undefined}
                  >
                    {row[columnIndex] || EMPTY_CELL_LABEL}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pageCount > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>
            Rows {page * PAGE_SIZE + 1}–
            {Math.min((page + 1) * PAGE_SIZE, rows.length)} of {rows.length}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded-md border border-gray-300 px-3 py-1 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={page === 0}
              onClick={() => setPage((current) => current - 1)}
            >
              Previous
            </button>
            <button
              type="button"
              className="rounded-md border border-gray-300 px-3 py-1 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={page >= pageCount - 1}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollDataTable;
